import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Gauge } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Card } from "@/components/Common";
import { Slider } from "@/components/ui/slider";
import { api, errMsg, tzOffset } from "@/lib/api";
import { setPassMin, usePassMin } from "@/lib/atsScores";
import { getAutoPick, setAutoPick } from "@/lib/tailoredLib";
import { WinsCard } from "@/components/profile/WinsCard";

const PREF = "rpopro_auto_recheck";
export const autoRecheckOn = () => localStorage.getItem(PREF) !== "off";

export const AtsAutomation = () => {
  const [auto, setAuto] = useState(autoRecheckOn);
  const [digest, setDigest] = useState(undefined);
  const passMin = usePassMin();
  const [autoPick, setAutoPickState] = useState(getAutoPick);
  useEffect(() => { api.get("/ats-scores/digest").then((r) => setDigest(typeof r.data?.enabled === "boolean" ? r.data.enabled : null)).catch(() => setDigest(null)); }, []);
  const toggleAuto = (v) => { setAuto(v); localStorage.setItem(PREF, v ? "on" : "off"); toast.success(v ? "Auto re-check on" : "Auto re-check off"); };
  const toggleDigest = async (enabled) => {
    try { await api.put("/ats-scores/digest", { enabled, tz_offset_minutes: tzOffset() }); setDigest(enabled); toast.success(enabled ? "Weekly score digest on" : "Weekly score digest off"); } catch (e) { if ([404, 405].includes(e.response?.status)) { setDigest(null); toast.message("The weekly digest turns on once the RPOPRO backend update goes live."); } else toast.error(errMsg(e)); }
  };
  const sendNow = () => api.post("/ats-scores/digest/send-now").then(() => toast.success("Digest sent to your inbox"), (e) => toast.error(errMsg(e)));
  return (
    <Card data-testid="ats-automation-card">
      <div className="mb-4 flex items-center gap-2"><Gauge className="h-4 w-4 text-[#D4AF37]" /><h3 className="font-display text-lg text-zinc-100">ATS automation</h3></div>
      <label className="flex items-start justify-between gap-4">
        <span><span className="block text-sm text-zinc-200">Auto re-check after resume changes</span><span className="text-xs text-zinc-500">Re-scores up to 10 tracked jobs each time you replace or switch your resume.</span></span>
        <Switch checked={auto} onCheckedChange={toggleAuto} data-testid="auto-recheck-switch" />
      </label>
      <label className="mt-5 flex items-start justify-between gap-4">
        <span><span className="block text-sm text-zinc-200">Offer my best tailored resume on Apply</span><span className="text-xs text-zinc-500">When a job has a tailored version, ask to switch to it before applying.</span></span>
        <Switch checked={autoPick} onCheckedChange={(v) => { setAutoPick(v); setAutoPickState(v); toast.success(v ? "Best-resume prompt on" : "Best-resume prompt off"); }} data-testid="auto-pick-switch" />
      </label>
      <label className="mt-5 flex items-start justify-between gap-4">
        <span><span className="block text-sm text-zinc-200">Weekly score digest email</span><span className="text-xs text-zinc-500">{digest === null ? "Turns on as soon as the RPOPRO backend update goes live." : "Every Monday: your ATS score gains across all tracked jobs."}</span></span>
        <Switch checked={!!digest} disabled={digest == null} onCheckedChange={toggleDigest} data-testid="score-digest-switch" />
      </label>
      {digest && <Button size="sm" variant="ghost" onClick={sendNow} className="mt-3" data-testid="score-digest-send-now">Send me one now</Button>}
      <div className="mt-5">
        <div className="flex items-center justify-between gap-4">
          <span><span className="block text-sm text-zinc-200">Pre-apply pass score</span><span className="text-xs text-zinc-500">Below this, we run an ATS check before you apply. At or above, jobs get a "Ready to apply" tick.</span></span>
          <span className="font-mono text-lg text-[#F3C649]" data-testid="pass-min-value">{passMin}%</span>
        </div>
        <Slider min={50} max={95} step={5} value={[passMin]} onValueChange={([v]) => setPassMin(v)} onValueCommit={([v]) => toast.success(`Pass score set to ${v}%`, { id: "pass-min" })} className="mt-3" data-testid="pass-min-slider" />
      </div>
      <WinsCard />
    </Card>
  );
};
